import { type Dispatch, type SetStateAction, useCallback, useRef } from "react";

/**
 * Coalesces high-frequency state updates to one React commit per animation frame.
 * Flush synchronously on mouseup/cleanup so final state is never lost.
 */
export function useRafCoalescedState<T>(setState: Dispatch<SetStateAction<T>>) {
  const pendingRef = useRef<T | null>(null);
  const rafIdRef = useRef<number>(0);

  const flush = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
    if (pendingRef.current !== null) {
      setState(pendingRef.current);
      pendingRef.current = null;
    }
  }, [setState]);

  const scheduleUpdate = useCallback(
    (value: T) => {
      pendingRef.current = value;
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = 0;
          if (pendingRef.current !== null) {
            setState(pendingRef.current);
            pendingRef.current = null;
          }
        });
      }
    },
    [setState]
  );

  return { scheduleUpdate, flush };
}
